/** @format */

import {useState} from 'react';

import * as R from 'ramda';
import Alert from '@mui/material/Alert';
import {Box, LinearProgress, Typography} from '@mui/material';

import {Trans, useTranslation} from 'react-i18next';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import {format} from 'date-fns';

import IssueTabsNew from 'components/tabs/IssueTabsNew';
import Breadcrumbs from 'components/Breadcrumbs';
import CheckGraph from 'components/graphs/CheckGraph';

import categoriesData from 'pages/Home/Main/Project/data/categories.json';

import {
  ChecksWithSensor,
  getChecksNames,
  groupChecksByName,
  groupedChecks,
} from 'helpers/checkHelpers';

import {SensorCategoriesBunchType} from 'types';

import {useCompanyContext} from 'contexts/CompanyContext';

import {ROUTES} from 'constants/ROUTES';

import {useRouteSensorPage} from 'helpers/hooks/useRouteSensorPage';

import {usePeriodSelector} from 'components/PeriodSelector/hooks/usePeriodSelector';

import ExcludedRow from '../ExcludedRow';
import NeedRegionsRow from '../NeedRegionsRow';
import Row from '../Row';
import {bunchClasses as classes, BunchRoot as Root} from './styles';
import {sortChecks} from '../helpers/sortChecks';
import {ReportButton} from '../ReportButton';
import {combineByCollectedOn} from './helpers/combineByCollectedOn';
import {useDeleteSensorExclusionMutation, useGetCurrentUserQuery} from '../graphql/__generated__';

type Tab = 'ALL' | 'IMPROVEMENTS' | 'RECOMMENDATIONS';

interface Category {
  name: string;
  title: string;
  description: string;
  sensors: string[];
}

interface Props {
  categoryName: SensorCategoriesBunchType;
}

const CategoryBunch = ({categoryName}: Props) => {
  const {t} = useTranslation();

  const {currentCompany} = useCompanyContext();
  const [activeTab, setActiveTab] = useState<Tab>('ALL');

  const {onSensorClick} = useRouteSensorPage();

  const {startDate, endDate} = usePeriodSelector({defaultPeriod: 'week'});

  const {loading, error, data, refetch} = useGetCurrentUserQuery({
    variables: {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    },
  });
  const [deleteSensorExclusion, {loading: deleteSensorExclusionLoading}] =
    useDeleteSensorExclusionMutation();

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser?.currentCompany) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const category = categoriesData.find(v => v.name === categoryName) as Category;

  const {sensorExclusions, lastChecks, regions, graphSensorChecks} =
    data.currentUser.currentCompany;

  const allChecks = R.pipe(
    groupChecksByName,
    R.filter<ChecksWithSensor>(v => category.sensors.includes(v.sensor.name)),
    R.filter<ChecksWithSensor>(v => {
      if (activeTab === 'ALL') return true;
      return activeTab === 'IMPROVEMENTS' ? v.sensor.isError : !v.sensor.isError;
    }),
  )(lastChecks);

  const {validChecks, excludedChecks, needRegionsChecks} = groupedChecks({
    allChecks,
    sensorExclusions,
    regions,
  });

  const renderInfoPlate = () => {
    const checksCount = R.sum(validChecks.map(v => v.checks));
    const foundCount = R.sum(validChecks.map(v => v.found));
    const newCount = R.sum(validChecks.map(v => v.new));

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box className={classes.infoPlate}>
          <Typography className={classes.infoTitle}>
            <Trans>Проверено объектов</Trans>
          </Typography>
          <Typography className={classes.infoValue}>{checksCount}</Typography>
        </Box>
        <Box className={classes.infoPlate}>
          <Typography className={classes.infoTitle}>
            <Trans>Рекомендации</Trans>
          </Typography>
          <Typography className={classes.infoValue}>{foundCount}</Typography>
        </Box>
        <Box className={classes.infoPlate}>
          <Typography className={classes.infoTitle}>
            <Trans>Новые</Trans>
          </Typography>
          <Typography className={classes.infoValue}>{newCount}</Typography>
        </Box>
      </Box>
    );
  };

  const handleChangeTab = (tab: string) => {
    setActiveTab(tab as Tab);
  };

  const handleClickRow = (check: ChecksWithSensor) => {
    onSensorClick(check.sensor.name);
  };

  const breadcrumbs = [
    {
      href: ROUTES.INDEX,
      title: <Trans>Проекты</Trans>,
    },
    {
      href: `/${ROUTES.HOME}`,
      title: <Trans>{currentCompany.name}</Trans>,
    },
    {
      href: '',
      title: <Trans>{t(category.title)}</Trans>,
    },
  ];

  const sensorsSearchParam = getChecksNames(validChecks);

  const handleStartSensor = async (e: React.MouseEvent, v: ChecksWithSensor) => {
    e.stopPropagation();

    const exclusionsId = sensorExclusions.find(
      exclusion => exclusion.sensorName === v.sensor.name,
    )?.id;

    if (exclusionsId) {
      await deleteSensorExclusion({variables: {id: exclusionsId}});

      refetch();
    }
  };

  const combinedGraphChecksByCollectedOn = combineByCollectedOn(graphSensorChecks);

  return (
    <Root data-testid='category-bunch'>
      <Box
        sx={{
          mt: 12,
          mb: 10,
        }}
      >
        <Breadcrumbs items={breadcrumbs} />
      </Box>
      <Box
        sx={{
          mt: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box className={classes.categoryIcon}>
            <AccountBalanceIcon fontSize='small' htmlColor='#fff' />
          </Box>
          <Typography className={classes.categoryTitle}>{t(category.title)}</Typography>
        </Box>
        <ReportButton sensors={sensorsSearchParam} />
      </Box>
      <Box
        sx={{
          mt: 6,
        }}
      >
        <Typography className={classes.categoryDescription}>{t(category.description)}</Typography>
      </Box>
      <Box
        sx={{
          mt: 14,
        }}
      >
        <Box
          className={classes.categorySubtitle}
          sx={{
            mb: 6,
          }}
        >
          <Trans>Статистика по категории</Trans>
        </Box>

        {renderInfoPlate()}

        <Box
          sx={{
            mt: 2,
          }}
        >
          <CheckGraph graphChecks={combinedGraphChecksByCollectedOn} />
        </Box>
      </Box>
      <Box
        sx={{
          mt: 14,
        }}
      >
        <Box
          className={classes.categorySubtitle}
          sx={{
            mb: 6,
          }}
        >
          <Trans>Активные сенсоры</Trans>
        </Box>

        <IssueTabsNew activeTab={activeTab} onChange={handleChangeTab} />

        <Box
          sx={{
            mt: 6,
            mb: 6,
          }}
        >
          {sortChecks(validChecks, t).map((v, i) => (
            <Row key={i} check={v} onClick={handleClickRow} />
          ))}

          {sortChecks(needRegionsChecks, t).map((v, i) => (
            <NeedRegionsRow key={i} check={v} onClick={handleClickRow} />
          ))}
        </Box>
      </Box>
      {excludedChecks.length ? (
        <Box
          sx={{
            mt: 14,
          }}
        >
          <Box
            className={classes.categorySubtitle}
            sx={{
              mb: 6,
            }}
          >
            <Trans>Остановленные сенсоры</Trans>
          </Box>

          <Box
            sx={{
              mt: 6,
              mb: 6,
            }}
          >
            {sortChecks(excludedChecks, t).map((v, i) => (
              <ExcludedRow
                key={i}
                check={v}
                onClick={handleClickRow}
                handleStartSensor={(e: React.MouseEvent) => handleStartSensor(e, v)}
                deleteSensorExclusionLoading={deleteSensorExclusionLoading}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Root>
  );
};

export {CategoryBunch};
